import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";

import ClearFilterBtn from "../../../application/ClearFilterBtn";

import Highcharts from 'highcharts/highstock';

import useDataSource from "../../../../hooks/useDataSource";

import {
    getDataColor
} from "../../../../utilities/ColorUtilities";

import useMainCanvasSize from "../../../../hooks/useMainCanvasSize";
import {useTranslation} from "react-i18next";
import useWindowSize from "../../../../hooks/useWindowSize";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";

import {findComponent} from "../../../../redux/page/pageActions";

import { styled, Card, CardHeader, CardContent, LinearProgress } from '@mui/material';

const PREFIX = 'WordcloudChart';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    cardHeader: `${PREFIX}-cardHeader`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none',
    }
}));

export default function WordcloudChart({id, configuration, xId, onLoading, onLoaded}) {

	const dispatch = useDispatch();
    const { t } = useTranslation();

    // App State
    const config = useSelector(state => state.config);
    const persist = useSelector(state => state.persist);
    const filters = useSelector(state => state.data.filters);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);

	const componentUpdated = useSelector(state => state.page.componentUpdated);

	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

    // Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');

    const [chart, setChart] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [sliceValue, setSliceValue] = useState(-2112);
    const [noDataMessage, setNoDataMessage] = useState("No Data");
	const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});
	const [resizedComponent, setResizedComponent] = useState(false);

	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const valueField = compConfig.dataSource?.fields?.valueField ? compConfig.dataSource?.fields?.valueField : '';
	const styles = compConfig?.element?.styles;
	const highchartOptions = compConfig?.highchartOptions ? compConfig?.highchartOptions : {};

	const chartRef = useRef(null);
	const dataSource = useDataSource(dataSourceName);
	const size = useMainCanvasSize();
	const windowSize = useWindowSize();

	// Loading
	useEffect(() => {
		if (dataSource.fetching === true) {
			if (isFunction(onLoading)) {
				onLoading();
			}
		}
	}, [dataSource.fetching]);

	// Loaded
	useEffect(() => {
		if (dataSource.fetching === false && dataSource.fetched === true) {
			if (isFunction(onLoaded)) {
				onLoaded();
			}
		}
	}, [dataSource.fetched]);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
				setResizedComponent(true);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Wordcloud Chart Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'WordcloudChart'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    useEffect(() => {

        // console.log('-----------------WORDCLOUD CHART---------STATUS->');
        // console.log('fetching: {0}'.format(dataSource.fetching));
        // console.log('fetched: {0}'.format(dataSource.fetched));
        // console.log('error: {0}'.format(dataSource.error));
        // console.log('data:', dataSource.data);

        if (dataSource.error) {
            setNoDataMessage("Error");
            setChartData([]);
        }
        else if (dataSource.data) {

            setNoDataMessage("No Data");
            let data = dataSource.data;

            // Update Data/Chart
            setChartData(data);
        }
    }, [dataSource.data, dataSource.fetching, dataSource.fetched, dataSource.error]);

    useEffect(() => {

        updateChart();

    }, [chartData, sliceValue, persist.themeLight]);

    useEffect(() => {
        const componentFilters = filters.find(f => {
            return f.id === id
        });
        if (!componentFilters && sliceValue !== -2112) {
            setSliceValue(-2112);
        }
    }, [filters]);

    useEffect( () => {
        if (chart) {
            // setTimeout required to accurately obtain chart.renderTo.clientHeight and chart.renderTo.clientWidth to perform the reflow
            setTimeout(function() {
                // console.log('reflow');
                chart.reflow();
				setResizedComponent(resizedComponent === true ? false : false);
			}, 300)
		}
	}, [size, windowSize, resizedComponent, cardHeaderStyle, cardContentStyle])

	function isFunction(functionToCheck) {
		return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
	}

    const getSeriesColor = (index, value, sliceValueToCompare) => {
        // Example ramping values
        if (sliceValueToCompare === null || sliceValueToCompare === -2112) {
            // There are no selected values so we just want to ramp the colors
            return getDataColor(index, value, config.data.colorMap);
        } else {
            // We have selected values so we want not selected or the ramp color
            if (value === sliceValueToCompare) {
                return getDataColor(index, value, config.data.colorMap);
            } else {
                return persist.themeLight ? '#767676' : '#c0c2c9';
            }
        }
    }

    const formatTooltip = (formatters, label, input) => {

        // console.log('--> WordcloudChart: formatTooltip()');
        // console.log('FORMATTERS: ', formatters);
        // console.log('LABEL: {0} ({1})'.format(label, typeof label));
        // console.log('INPUT: {0} ({1})'.format(input, typeof input));

        let tooltip;
        if (label) {
            tooltip = "<tr><td colspan='2' style='font-weight: bold; text-align:center'>" + label + "</td></tr>";
        } else {
            tooltip = "<tr><td style='text-align:left'>" + "Occurrences:" + "</td><td style='text-align:right'><span style='font-weight:bold'>" + input + "</td></tr>";
        }

        // console.log('TOOLTIP: {0} ({1})'.format(tooltip, typeof tooltip));
        return tooltip;
    }

    const updateChart = () => {

        const data = chartData;

        if (!chart && data === null)
            return;

        // if (chart && data === null) {
        //     // No Data
        //     chart.update({
        //         // series: [{
        //         //     name: '{0}'.format(categoryField),
        //         //     data: []
        //         // }]
        //     }, true, true);
        //     chart.showNoData();
        //     return;
        // }

        // Setup series
        // console.log('> Setup series ...');
        // console.log('  - All values ...');
        let allValues = [];
        for (let i = 0; i < data.length; i++) {
            const item = data[i];
            // console.log('    > ITEM: ', item);
            if (item[valueField] !== null) {
                let itemPart = item[valueField].split(',');
                // console.log('    > ITEM PART: ', itemPart);
                for (let j = 0; j < itemPart.length; j++) {
                    allValues.push(itemPart[j].trim());
                }
            }
        }
        // console.log('      > Array: ', allValues);

        const distinctValues = [...new Set(allValues)];
        // console.log('  - Distinct values ...');
        // console.log('      > Array: ', distinctValues);

        let seriesData = [];
        for (let k = 0; k < distinctValues.length; k++) {
            let count = 0
            for (let l = 0; l < allValues.length; l++) {
                if (distinctValues[k] === allValues[l]) {
                    count = count + 1;
                }
            }
            seriesData.push({
                name: distinctValues[k],
                weight: count,
                // TODO: Perhaps a config option to use the default random colors??
                color: getSeriesColor(k, distinctValues[k], -2112)
            });
        }
        // console.log('  - Series Data ...');
        // console.log('      > Array: ', seriesData);

        const series = [];
        series.push({
            type: 'wordcloud',
            data: seriesData
        });
        // console.log('  - Series: ', series);

        if (chart && data === null) {
            // No Data
            chart.update({
                series: series
            }, true, true);
            chart.showNoData();
            return;
        }

        if (!chart) {
            // console.log('** ---------- WORDCLOUD: CREATE Chart ----------**');

            const chartOptions = {
                chart: {
                    // type: 'wordcloud',
                    // animation: false
                    backgroundColor: null
                },
                credits: {
                    enabled: false
                },
                // Card Attribution Used
                title: {
                    text: '',
                    margin: 0
                },
                subtitle: {
                    text: '',
                    margin: 0
                },
                lang: {
                    noData: noDataMessage
                },
                noData: {
                    style: {
                        fontWeight: 'bold',
                        fontSize: '15px',
                        color: '#303030'
                    }
                },
                legend: {
                    ...(highchartOptions.legend ? highchartOptions.legend : {}),
                    itemStyle: {
                        color: '#727272',
                        fontWeight: 'bold'
                    }
                },
                plotOptions: {
                    ...(highchartOptions.plotOptions ? highchartOptions.plotOptions : {}),
                },
                series: series,
                tooltip: {
                    ...(highchartOptions.tooltip ? highchartOptions.tooltip : {}),
                    borderColor: '#000000',
                    shared: true,
                    useHTML: true,
                    formatter: function() {
                        // console.log(' > WordcloudChart: CREATE Chart: tooltip.formatter(): ', this);
                        let formattedTooltip = '<table>';
                        formattedTooltip += formatTooltip(null, this.points[0].key, null);
                        formattedTooltip += formatTooltip(null, null, this.points[0].point.weight);
                        formattedTooltip += '</table>';
                        return formattedTooltip;
                    }
                }
            };

            // console.log('** ---------- WORDCLOUD: CREATE Chart (chartOptions) ----------**', chartOptions);
            setChart(Highcharts.chart(chartRef.current, chartOptions));
        } else {
            // console.log('** ---------- WORDCLOUD: UPDATE Chart ----------**');

            chart.update({
                // NOTE: We need to update our handlers otherwise they will point to
                // a previously defined function with a context that does not match
                // the current state - to reproduce comment out plot options
                // filter once chart, clear, then filter the 2nd chart and all
                // previously applied filters will be displayed
                // series: series,
            }, true);
        }
    };

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {...styles?.cardContent
			,height: 'calc(100% - 49px)'
			,overflow: 'auto'
			,padding: '0px'}
		);

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}


	return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
            <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
            <div className={classes.linearProgress}></div>
            }
            <Card style={{height: '100%', boxShadow: 'none', ...styles?.card}}>
                <CardHeader action={ClearFilterBtn(id, false)} className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
                            subheader={t(subtitle)}>
				</CardHeader>
                <CardContent style={cardContentStyle}>
                    <div id={"wcc"} ref={chartRef} style={{position: 'relative', top: '0px', width: '100%', height: '100%'}}></div>
                </CardContent>
            </Card>
        </Root>
    );
}
